// Язык игры: какой выбран, где записан выбор и как подобрать язык браузеру.
//
// Переводы лежат готовыми паками в assets/locales-base, по папке на язык. Прочитать список
// папок в браузере нечем: они уходят в статику, а не в сборку. Поэтому список записан здесь,
// а тест сверяет его с диском.
//
// Выбор игрока живёт в local storage и переживает перезагрузку. На первом заходе выбора нет,
// и язык подбирается по языкам браузера. Не подошёл ни один — игра идёт на английском.
// Подобранный язык записывается сразу: дальше игра идёт на том, на котором началась, даже
// если игрок сменит язык браузера.

/** Языки, на которые игра переведена. Имя языка — это имя папки в assets/locales-base. */
export const LOCALES = [
  'bg_BG',
  'cs_CZ',
  'da_DK',
  'de_DE',
  'el_GR',
  'en_GB',
  'es_ES',
  'et_EE',
  'fi_FI',
  'fr_FR',
  'hu_HU',
  'is_IS',
  'it_IT',
  'ja_JA',
  'ko_KR',
  'lt_LT',
  'lv_LV',
  'nl_NL',
  'nn_NO',
  'pl_PL',
  'pt_BR',
  'pt_PT',
  'ro_RO',
  'ru_RU',
  'sk_SK',
  'sr_SP',
  'sv_SE',
  'th_TH',
  'tr_TR',
  'vi_VN',
  'zh_CN',
] as const satisfies readonly string[];

export type Locale = (typeof LOCALES)[number];

/** Названия языков на самих языках: игрок узнаёт свой язык, даже когда интерфейс ему чужой. */
export const LOCALE_NAMES = {
  bg_BG: 'Български',
  cs_CZ: 'Čeština',
  da_DK: 'Dansk',
  de_DE: 'Deutsch',
  el_GR: 'Ελληνικά',
  en_GB: 'English (UK)',
  es_ES: 'Español',
  et_EE: 'Eesti',
  fi_FI: 'Suomi',
  fr_FR: 'Français',
  hu_HU: 'Magyar',
  is_IS: 'Íslenska',
  it_IT: 'Italiano',
  ja_JA: '日本語',
  ko_KR: '한국어',
  lt_LT: 'Lietuvių',
  lv_LV: 'Latviešu',
  nl_NL: 'Nederlands',
  nn_NO: 'Norsk nynorsk',
  pl_PL: 'Polski',
  pt_BR: 'Português (Brasil)',
  pt_PT: 'Português (Portugal)',
  ro_RO: 'Română',
  ru_RU: 'Русский',
  sk_SK: 'Slovenčina',
  sr_SP: 'Српски',
  sv_SE: 'Svenska',
  th_TH: 'ไทย',
  tr_TR: 'Türkçe',
  vi_VN: 'Tiếng Việt',
  zh_CN: '简体中文',
} as const satisfies Record<Locale, string>;

/**
 * Язык по умолчанию.
 *
 * На него же игра сходит за надписью, которой в выбранном языке не нашлось, см. i18n/text.ts.
 */
export const DEFAULT_LOCALE: Locale = 'en_GB';

/** Ключ, под которым выбор игрока лежит в local storage. */
const STORAGE_KEY: string = 'slot.locale';

/** Знает ли игра такой язык. */
export function isLocale(name: string): name is Locale {
  return (LOCALES as readonly string[]).includes(name);
}

/** Человеко-читаемое название языка для списка настроек. */
export function localeName(locale: Locale): string {
  return LOCALE_NAMES[locale];
}

/**
 * Приводит языковой тег браузера к имени папки перевода.
 *
 * Браузер называет язык тегом BCP 47 вида en-GB, а папки названы через подчёркивание.
 * Регистр в теге браузер не держит, поэтому язык идёт строчными, а страна заглавными.
 */
function folderName(tag: string): string {
  const [language, region]: string[] = tag.split(/[-_]/);

  if (region === undefined)
    return language.toLowerCase();

  return `${language.toLowerCase()}_${region.toUpperCase()}`;
}

/**
 * Подбирает язык под список желаемых, от самого желанного к менее.
 *
 * Сначала игра ищет точное совпадение со страной: игрок из Бразилии получит pt_BR, а не pt_PT.
 * Не нашлось — идёт совпадение по языку без страны, и тогда берётся первая страна из списка.
 * Так en-US приводит к en_GB, а перевода без страны в паках просто нет.
 *
 * @param wanted Языковые теги браузера, например navigator.languages.
 * @returns Имя папки перевода. Null — ни один из желаемых языков игра не знает.
 */
export function matchLocale(wanted: readonly string[]): Locale | null {
  const names: string[] = wanted.map(folderName);

  for (const name of names) {
    if (isLocale(name))
      return name;
  }

  for (const name of names) {
    const language: string = name.split('_')[0];
    const found: Locale | undefined = LOCALES.find((locale: Locale) => locale.split('_')[0] === language);

    if (found !== undefined)
      return found;
  }

  return null;
}

/**
 * Читает выбор игрока.
 *
 * Хранилище может быть закрыто настройками браузера, и обращение к нему тогда бросает.
 * Игру это ронять не должно: без записи она просто подберёт язык заново.
 *
 * @returns Выбранный язык. Null — выбора нет, или записан язык, которого игра не знает.
 */
export function readLocale(): Locale | null {
  try {
    const saved: string | null = localStorage.getItem(STORAGE_KEY);

    return saved !== null && isLocale(saved) ? saved : null;
  } catch (error: unknown) {
    console.warn('Local storage закрыт, выбор языка не прочитать', error);
    return null;
  }
}

/** Записывает выбор игрока. Закрытое хранилище игру не роняет: выбор просто не переживёт вкладку. */
export function writeLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch (error: unknown) {
    console.warn('Local storage закрыт, выбор языка не записать', error);
  }
}

/**
 * Язык, на котором игре идти.
 *
 * Записанный выбор идёт как есть. Выбора нет — язык подбирается по браузеру и тут же
 * записывается: игрок должен видеть один и тот же язык от захода к заходу.
 */
export function pickLocale(): Locale {
  const saved: Locale | null = readLocale();
  if (saved !== null)
    return saved;

  const picked: Locale = matchLocale(navigator.languages) ?? DEFAULT_LOCALE;
  writeLocale(picked);

  return picked;
}
